// OSS donations data layer (arc 101 slice 11, plan doc 05) — the SPA's half of
// `/v1/donations` (`apps/api/src/routes/donations.ts`).
//
// **Two sides of one gift, two hooks.** `useDonations` reads what the caller
// GAVE; `useDonationsReceived` reads what repos the caller OWNS were given.
// Neither is derivable from the other and the API authorizes them against
// different principals, so they never share a query key: a user who donates from
// their personal account and maintains under an org sees each side under the
// right `X-Dz-Account` scope. Both key on `activeAccountId()` for that reason.
//
// Writes are bare functions, not mutations — the `lib/fleet.ts` posture. Every
// caller re-READS after a write (the paged primitive's `reload()`), because the
// authority for a gift's state is the row, not the echo: a `money` gift comes
// back `active: false` until Stripe's webhook flips it, so patching the list from
// a POST response would publish a donor who has not paid yet.

import type { DonationKind, DonationTarget } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

/**
 * One gift as the API projects it (`DonationBody`, `routes/donations.ts`
 * `toView`) — byte-identical to the `donation` MCP verb's mask, so the same row
 * renders whichever door read it.
 *
 * `active: false` is a PARKED gift, not a deleted one: a donor detached it, the
 * donor's subscription ended, or (on a `money` gift) Stripe has not confirmed the
 * payment yet. The inbox renders parked rows on purpose — capacity vanishing
 * silently is the failure this surface exists to prevent.
 */
export type DonationRow = {
  readonly id: string;
  readonly kind: DonationKind;
  readonly target_kind: DonationTarget;
  readonly target_account_id: string | null;
  readonly target_repo_id: string | null;
  /**
   * The pinned project's `owner/repo`, DERIVED server-side from `repositories`
   * (arc 101 slice 11). Absent — never null — on a `pool` gift and on a `project`
   * gift whose repo row is gone, mirroring the projection's optional-field
   * convention.
   *
   * A donor funds repos they do NOT own, so the id alone can name nothing here:
   * `/v1/repos` is account-scoped and the dashboard's own repo route would 404
   * for them. This is what links the gift to its public `/oss/<full_name>` page.
   */
  readonly target_repo_full_name?: string;
  /**
   * The GIVER's GitHub login, on the recipient's read only (a donor knows who
   * they are). Absent — never null — when the donor account no longer resolves.
   *
   * A login is the ONLY donor identity this surface ever carries: an email is
   * never projected, at any layer, so the inbox cannot leak one by rendering a
   * field it was handed.
   */
  readonly donor_login?: string;
  readonly runner_id: string | null;
  readonly byok_key_id: string | null;
  /** The RECIPIENT's model pick on an `llm` gift, inside the donor's ceiling. Null = unpicked. */
  readonly chosen_model: string | null;
  /**
   * The donor's model CEILING on an `llm` gift, RESOLVED server-side: their
   * `allowed_models`, or the key's own single model for the legacy `null` shape.
   * Absent on money/compute and on a gift whose key is gone.
   *
   * The picker's vocabulary, never the authorization — the port refuses a pick
   * outside it (422 naming what is permitted) and the pool resolver re-applies
   * it at seal time, so nothing here decides what may run.
   */
  readonly allowed_models?: readonly string[];
  readonly active: boolean;
  readonly created_iso: string;
  readonly revoked_iso: string | null;
  /** The donated key's spend cap, when the projection carried one. */
  readonly key_spend_cap_usd?: number;
};

/**
 * One entry the maintainer's model picker may offer — the platform CATALOG
 * (`MODEL_REGISTRY`), served by the API because the SPA cannot import
 * `@developerz/config`. It is a vocabulary, never an authorization: the donor's
 * `allowed_models` ceiling stays server-side and a pick outside it is a 422.
 */
export type ModelOption = { readonly provider: string; readonly model: string };

/** `GET /v1/donations/received` — a page of gifts PLUS the picker's vocabulary. */
export type ReceivedPage = Page<DonationRow> & { readonly model_options: readonly ModelOption[] };

export const DONATIONS_QUERY_KEY = 'donations';
export const DONATIONS_RECEIVED_QUERY_KEY = 'donations_received';

/**
 * Both donation lists' poll cadence — `usePagedQuery`'s `poll.every`, which
 * re-walks every loaded page. Never the query's `refetchInterval`: that is keyed
 * on the cursor and re-read only the last page (plan 101 row 17-21).
 */
export const DONATIONS_POLL_MS = 30_000;

/**
 * One page of the caller's OWN gifts, newest-first. Idle with no session.
 * Parked rows are kept — a donor needs to see the gift Stripe has not confirmed,
 * and the one their cancelled subscription parked.
 *
 * Polled by the caller's `usePagedQuery` ({@link DONATIONS_POLL_MS},
 * {@link fetchDonationsPage}): a `money` gift is `active: false` until
 * `checkout.session.completed` reaches the worker, so the row (not the
 * `?donated=1` redirect) is what says the subscription is live.
 */
export function useDonations(cursor: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<Page<DonationRow>>(() => ({
    queryKey: [DONATIONS_QUERY_KEY, cursor(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => fetchDonationsPage(cursor(), token),
  }));
}

/** One page of the caller's own gifts — {@link useDonations}'s read and its poll's. */
export function fetchDonationsPage(
  cursor: string | null,
  token: string | null,
): Promise<Page<DonationRow>> {
  return apiFetch<Page<DonationRow>>(withCursor('/v1/donations', cursor), token);
}

/**
 * One page of the MAINTAINER's inbox — gifts pinned to repos this account owns —
 * plus `model_options`. Idle with no session. Authorized against the recipient
 * server-side (a `repositories` JOIN), so nothing here re-checks ownership.
 *
 * The extra `model_options` member rides the same body: `ReceivedPage` widens
 * `Page<DonationRow>`, so this query still feeds `usePagedQuery` unchanged.
 */
export function useDonationsReceived(
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<ReceivedPage>(() => ({
    queryKey: [DONATIONS_RECEIVED_QUERY_KEY, cursor(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => fetchDonationsReceivedPage(cursor(), token),
  }));
}

/** One inbox page — {@link useDonationsReceived}'s read and its poll's. */
export function fetchDonationsReceivedPage(
  cursor: string | null,
  token: string | null,
): Promise<ReceivedPage> {
  return apiFetch<ReceivedPage>(withCursor('/v1/donations/received', cursor), token);
}

/**
 * `POST /v1/donations` body. The kind↔ref invariants are the DOMAIN's and are
 * refused at the API edge with a field path — this type mirrors them so a
 * mismatched ref (a `byok_key_id` on a `compute` gift) is a compile error here
 * rather than a 400 at runtime.
 *
 * `models` is the donor's model CEILING on a donated key: a concrete list, or
 * `['*']` for any model the endpoint serves. Omit it to leave the key's stored
 * ceiling untouched — a present `null` would silently reset a multi-model key.
 */
export type CreateDonationInput =
  | { readonly kind: 'money'; readonly target: 'pool' }
  | { readonly kind: 'money'; readonly target: 'project'; readonly repo_full_name: string }
  | { readonly kind: 'compute'; readonly target: 'pool'; readonly runner_id: string }
  | {
      readonly kind: 'compute';
      readonly target: 'project';
      readonly repo_full_name: string;
      readonly runner_id: string;
    }
  | {
      readonly kind: 'llm';
      readonly target: 'pool';
      readonly byok_key_id: string;
      readonly models?: readonly string[];
    }
  | {
      readonly kind: 'llm';
      readonly target: 'project';
      readonly repo_full_name: string;
      readonly byok_key_id: string;
      readonly models?: readonly string[];
    };

/**
 * `POST /v1/donations` 201 body — the created row, plus `checkout_url` on a
 * `money` gift.
 *
 * `checkout_url` is a URL or it is ABSENT — never null. A deployment that mints
 * no hosted Stripe session refuses the money gift outright rather than recording
 * one nobody can pay for (101 sweep OSS-3), so the null that used to mean
 * "subscribe through the billing portal instead" has no state left to describe.
 * Absent entirely on compute/llm: nothing to buy.
 */
export type CreatedDonation = DonationRow & { readonly checkout_url?: string };

/**
 * Record one gift. A **402** is the missing $50/mo `oss_donor` subscription (the
 * paywall on compute/llm gifts), a **422** a target/resource/ceiling refusal,
 * and a **503** `donation-checkout-unavailable` a `money` gift on a deployment
 * that mints no hosted donor checkout (nothing is recorded). All three carry the
 * server's own sentence as the problem `detail`, which is the line to render
 * (`errorReason`, via `createMutation` in `DonatePayStep`). None of that is
 * re-derived here.
 *
 * A `money` gift answers `active: false` alongside its `checkout_url`, and that
 * is the contract: the gift is PENDING until Stripe says it was paid. Poll the
 * row; never read the `?donated=1` redirect as proof.
 */
export function createDonation(
  input: CreateDonationInput,
  token: string | null = SESSION_TOKEN,
): Promise<CreatedDonation> {
  return apiFetch<CreatedDonation>('/v1/donations', token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Detach a gift the CALLER gave: parks the row and clears the resource's
 * earmark, so the box/key rejoins ordinary scheduling. Authorized as the DONOR —
 * a maintainer cannot revoke a gift they were given, and an unknown id, an
 * already-detached one and somebody else's are one 404.
 */
export function detachDonation(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<DonationRow> {
  return apiFetch<DonationRow>(`/v1/donations/${encodeURIComponent(id)}`, token, {
    method: 'DELETE',
  });
}

/**
 * Refuse a gift the caller's project was GIVEN: parks the row and releases the
 * donor's box/key back to their own fleet. Authorized as the RECIPIENT — the
 * mirror of {@link detachDonation}, which is the donor's.
 *
 * Both halves exist because a gift binds two parties: someone else's machine
 * running against your repository is a trust decision, so "revoke" belonging
 * only to the giver would leave a maintainer with no way out. An unknown id, an
 * already-parked gift and one pinned to somebody else's repo are one 404.
 */
export function declineDonation(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<DonationRow> {
  return apiFetch<DonationRow>(`/v1/donations/${encodeURIComponent(id)}/decline`, token, {
    method: 'POST',
  });
}

/**
 * The RECIPIENT's model pick on a donated `llm` gift. `null` CLEARS the pick and
 * is sent explicitly — the field is required server-side, because clearing is a
 * choice a maintainer makes deliberately, never one a client makes by omission.
 *
 * The donor's ceiling is enforced in the port: a pick outside it is a 422 naming
 * what is permitted, and the pool resolver re-applies the ceiling at seal time.
 */
export function chooseDonationModel(
  id: string,
  model: string | null,
  token: string | null = SESSION_TOKEN,
): Promise<DonationRow> {
  return apiFetch<DonationRow>(`/v1/donations/${encodeURIComponent(id)}/model`, token, {
    method: 'PATCH',
    body: JSON.stringify({ model }),
  });
}
